import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

interface PermissionMap {
    [key: string]: string[]; // e.g., 'obras': ['ver', 'editar']
}

interface User {
    id: number;
    nome: string;
    email: string;
    cargo_nome: string;
    nivel_acesso?: string; // Optional as legacy users might miss it
    permissoes_json: PermissionMap | null;
    conta_id: number;
    status_assinatura?: 'pendente' | 'trial' | 'ativo' | 'atrasado' | 'bloqueado' | 'expirado' | 'suspenso';
    data_expiracao?: string;
    plano_tipo?: 'gratuito' | 'basico' | 'completo';
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (token: string, userData: User) => void;
    logout: () => void;
    hasPermission: (module: string, action?: 'ver' | 'editar' | 'excluir') => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        console.log('[AuthContext] Effect started');

        const initializeAuth = async () => {
            const token = localStorage.getItem('token');

            if (!token) {
                console.log('[AuthContext] No token found');
                if (isMounted) setLoading(false);
                return;
            }

            console.log('[AuthContext] Token found');
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            // 1. Try Local Storage Cache
            const cachedUser = localStorage.getItem('user');
            if (cachedUser) {
                try {
                    const parsed = JSON.parse(cachedUser);
                    if (isMounted) setUser(parsed);
                } catch (e) {
                    console.error('[AuthContext] Error parsing user cache', e);
                    localStorage.removeItem('user');
                }
            }

            // 2. Validate with API
            try {
                console.log('[AuthContext] Validating token with API...');
                // Corrected path to /api/users/me
                const response = await api.get('/api/users/me');
                if (isMounted) {
                    console.log('[AuthContext] API success');
                    // API returns { status: 'success', data: { ...user } }
                    // Axios response.data is the JSON body.
                    const body = response.data;
                    const userData = body.data || body; // Fallback just in case

                    setUser(userData);
                    localStorage.setItem('user', JSON.stringify(userData));
                }
            } catch (error: any) {
                if (isMounted) {
                    console.error('[AuthContext] API validation failed', error);
                    // If 404, it means the token is valid format but user ID doesn't exist in DB (e.g. deleted or dev environment reset)
                    if (error.response && error.response.status === 404) {
                        console.warn('[AuthContext] User not found (404). Clearing session.');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        setUser(null);
                        // No need to redirect if we just render "not authenticated" state, but logout() helper does it.
                    }
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        initializeAuth();

        return () => {
            console.log('[AuthContext] Effect cleanup');
            isMounted = false;
        };
    }, []);

    const login = (token: string, userData: User) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        window.location.href = '/login';
    };

    const hasPermission = (module: string, action: 'ver' | 'editar' | 'excluir' = 'ver'): boolean => {
        if (!user) return false;

        // Robust Admin Bypass
        // 1. Check strict 'admin' level from backend
        if (user.nivel_acesso === 'admin') return true;

        // 2. Check Role Name (case insensitive, common variations)
        const role = user.cargo_nome ? user.cargo_nome.toLowerCase() : '';
        if (role === 'admin' || role === 'administrador') return true;

        if (!user.permissoes_json) return false;

        // 1. Exact Match Check
        const modulePerms = user.permissoes_json[module];
        if (modulePerms && (modulePerms.includes(action) ||
            (action === 'ver' && (modulePerms.includes('editar') || modulePerms.includes('excluir'))) ||
            (action === 'editar' && modulePerms.includes('excluir')))) {
            return true;
        }

        // 2. Granular/Prefix Check for "ver" access (Visibility)
        // If I request 'pcm', and I have 'pcm.ativos', should I have access?
        // Generally, for 'Visibility' (showing the menu), yes.
        // For 'Editing', no, unless I strictly have 'pcm' edit.
        // But the MenuBar asks for 'ver'.
        if (action === 'ver') {
            // Check if any key starts with module + '.'
            // Optimization: iterate keys
            const keys = Object.keys(user.permissoes_json);
            const hasSub = keys.some(k => k.startsWith(module + '.'));
            if (hasSub) return true;
        }

        return false;
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, login, logout, hasPermission }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
